import React, { ReactNode } from "react";
import { get, useFormContext, Controller } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { classNames } from "helpers/commonHelpers";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import { useReviewFieldErrorClear } from "@RequestFormV2/hooks/useReviewFieldErrorClear";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import "./Input.scss";

interface InputProps {
  /**
   * Renders an icon on the left side of the input.
   */
  icon?: ReactNode;
  /**
   * Renders an icon on the right side of the input.
   */
  iconRight?: ReactNode;
  /**
   * Disables the input element if true.
   */
  disabled?: boolean;
  /**
   * Label text associated with the input element.
   */
  label?: string;
  /**
   * Additional text or description that provides hints or additional information rendered under the input element.
   */
  hintText?: string;
  /**
   * Sets the width of the input element. Can be specified as a string (e.g., "100px", "50%") or a number (e.g., 100 for 100px).
   */
  width?: string | number;
  /**
   * Necessary when using the input element with React-hook-form. Specifies the name of the input element.
   */
  name?: string;
  /**
   * Necessary when not using React-hook-form Callback function triggered when the value of the input changes.
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Callback triggered when content is pasted into the input. Useful for intercepting
   * spreadsheet-style multi-cell pastes.
   */
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  /**
   * Placeholder text displayed in the input element when it is empty.
   */
  placeholder?: string;
  /**
   * Indicates whether the input field is required.
   */
  required?: boolean;
  /**
   * Object specifying validation rules for the input element.
   */
  registerOptions?: object;
  /**
   * Default value for the input element.
   */
  defaultValue?: string | number;
  /**
   * Error object returned by React-hook-form when the input value is invalid.
   */
  errorMessage?: string;
  /**
   * Applies error outline styling without showing an error message.
   */
  hasError?: boolean;
  /**
   * Keeps the RHF error for form validity but hides the red outline and message.
   */
  hideError?: boolean;
  /**
   * Value of the input element when not using React-hook-form
   */
  value?: string | number;
  /**
   * If true, the input field will be unregistered when unmounted.
   */
  shouldUnregister?: boolean;
  /**
   * ID of the input element.
   */
  id?: string;
  /**
   * Indicates if the input is read-only.
   */
  readOnly?: boolean;
  /**
   * Specifies the type of the input element. This can be any valid HTML input type, such as "text", "password", "email", etc.
   * Default is "text".
   */
  type?: string;
  /**
   * data-testid for the input element.
   */
  testId?: string;
  /**
   * When true (default), saves the field on blur if RequestFormSaveProvider is present.
   */
  saveOnBlur?: boolean;
  /**
   * Optional callback invoked after the field blur handler runs (RHF onBlur).
   */
  onAfterBlur?: () => void;
  /**
   * When provided, normalizes the raw input value before passing to RHF onChange.
   */
  normalizeValue?: (raw: string) => string;
  /**
   * When true, runs field validation synchronously after each change.
   */
  validateOnChange?: boolean;
  /**
   * When true, skips post-Review reactive error clearing for this field.
   */
  disableReviewErrorClear?: boolean;
}

const Input = function ({
  id,
  icon,
  iconRight,
  disabled,
  label,
  hintText,
  width,
  name,
  onChange,
  onPaste,
  placeholder,
  required,
  registerOptions,
  defaultValue,
  errorMessage,
  hasError = false,
  hideError = false,
  value,
  shouldUnregister,
  readOnly,
  type,
  testId,
  saveOnBlur = true,
  onAfterBlur,
  normalizeValue,
  validateOnChange = false,
  disableReviewErrorClear = false,
}: InputProps) {
  const resolvedTestId = testId || (name ? `form-field-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}` : undefined);
  const formContext = useFormContext();
  const control = formContext ? formContext.control : null;
  const trigger = formContext?.trigger;
  const errors = formContext?.formState?.errors ?? {};
  const saveContext = useRequestFormSaveOrNull();
  const clearReviewError = useReviewFieldErrorClear(disableReviewErrorClear ? undefined : name);

  const renderInput = (fieldProps: any = {}, error: any = null) => {
    const visibleError = hideError ? null : error;

    return (
      <div className="inputV2" style={{ width }}>
        {label && (
          <label className={classNames({ required })}>{label}</label>
        )}
        <div className={`input-container ${visibleError || hasError ? "error" : ""} ${disabled ? "disabled" : ""}`}>
          {icon}
          <input
            id={id}
            type={type}
            style={{ width: "100%" }}
            data-testid={resolvedTestId}
            {...fieldProps}
            disabled={disabled}
            value={fieldProps.value !== undefined ? fieldProps.value : value}
            onChange={(e) => {
              const nextValue = normalizeValue ? normalizeValue(e.target.value) : e;
              if (fieldProps.onChange) fieldProps.onChange(nextValue);
              if (onChange) onChange(e);
              if (!disableReviewErrorClear) clearReviewError(e.target.value);
              if (validateOnChange && name && trigger) {
                trigger(name).catch(() => undefined);
              }
            }}
            onBlur={(e) => {
              if (fieldProps.onBlur) fieldProps.onBlur(e);
              if (saveOnBlur && name && saveContext) {
                saveContext.saveField(name, e.target.value).catch(() => undefined);
              }
              onAfterBlur?.();
            }}
            onPaste={onPaste}
            placeholder={placeholder}
            readOnly={readOnly}
          />
          {iconRight}
        </div>
        {visibleError && (
          <div className="text-sm error-text text-error-primary">
            {visibleError.message ? (
              visibleError.message
            ) : (
              <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
            )}
          </div>
        )}
        {hintText && !visibleError && <div className="text-sm hint-text">{hintText}</div>}
      </div>
    );
  };

  if (name && control) {
    return (
      <>
        {!disableReviewErrorClear && <ReviewFieldErrorClearWatcher fieldPath={name} />}
        <Controller
          name={name}
          control={control}
          shouldUnregister={shouldUnregister}
          rules={{ required, ...registerOptions }}
          defaultValue={defaultValue}
          render={({ field, fieldState }) => {
            const error = fieldState.error ?? (name ? get(errors, name) : undefined);
            return renderInput(field, error);
          }}
        />
      </>
    );
  }

  return renderInput({}, errorMessage && { message: errorMessage });
};

Input.defaultProps = {
  id: undefined,
  shouldUnregister: false,
  icon: undefined,
  iconRight: undefined,
  errorMessage: undefined,
  disabled: false,
  width: "100%",
  name: "",
  placeholder: "",
  label: "",
  hintText: "",
  required: false,
  registerOptions: {},
  defaultValue: "",
  value: undefined,
  onChange: () => {},
  readOnly: false,
  type: "text",
};

export default Input;
